import {BillList, SepaRemittanceList, SepaRemittanceRestControllerApiFactory} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import {useApiManager} from "../../../../components/utils/api";
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {EmptyState, Panel, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {dateToString} from "../../../../components/utils/dates";
import {REMITTANCE_STATUS} from "./labels";
import {useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import dayjs from "dayjs";
import {CustomNumberFormat} from "../../../../components/utils/custom_number_format";


/** Remesas de adeudos SEPA (#12847) y los recibos devueltos de los últimos 90 días (#12848). */
export const SepaRemittanceListPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(SepaRemittanceRestControllerApiFactory);
	const navigate = useNavigate();
	const [returned, setReturned] = useState<BillList[]>([]);

	useEffect(() => {
		const from = dayjs().subtract(90, "day").format("YYYY-MM-DD");
		apiManager.execute(ctrl.returnedSepaRemittance(from)).then((bills) => setReturned(bills as BillList[]));
	}, []);

	const prepare = () => {
		const suggested = dayjs().add(1, "month").startOf("month").add(4, "day").format("YYYY-MM-DD");
		const date = prompt(
			"Fecha de cargo (AAAA-MM-DD). Entran las facturas domiciliadas sin cobrar que vencen como " +
			"tarde ese día. Tiene que ser posterior a hoy.", suggested);
		if (!date) {
			return;
		}
		apiManager
		.execute(ctrl.prepareSepaRemittance(date), "Remesa preparada")
		.then((r) => navigate(`/app/admin/billing/sepa-remittance/${r.id}`))
		.catch(() => undefined); // el motivo ya lo enseña el aviso de apiManager
	};

	const listConfig: AppListConfig<SepaRemittanceList> = {
		title: "Remesas SEPA",
		onClick: "/app/admin/billing/sepa-remittance",
		headers: [
			{title: "Remesa"},
			{title: "Cargo"},
			{title: "Facturas", align: "right"},
			{title: "Importe", align: "right"},
			{title: "Estado"},
		],
		decorator: (bean: SepaRemittanceList) => (
			<>
				<TableCell sx={{whiteSpace: "nowrap"}}>{bean.messageId}</TableCell>
				<TableCell sx={{whiteSpace: "nowrap"}}>{dateToString(bean.collectionDate)}</TableCell>
				<TableCell align={"right"} sx={{fontVariantNumeric: "tabular-nums"}}>{bean.billCount}</TableCell>
				<TableCell align={"right"} sx={{whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>
					<CustomNumberFormat amount={bean.totalAmount}/> €
				</TableCell>
				<TableCell sx={{whiteSpace: "nowrap"}}>
					<SoftChip tone={REMITTANCE_STATUS[bean.status || ""]?.tone || "neutral"}
							  label={REMITTANCE_STATUS[bean.status || ""]?.label || bean.status}/>
					{bean.presentedDate && (
						<Box component={"span"} color={"text.secondary"} sx={{ml: 1, fontSize: 12}}>{dateToString(bean.presentedDate)}</Box>
					)}
				</TableCell>
			</>
		),
		supplier: async (currentPage, size, term, showDismissed) => {
			// Parámetros POSICIONALES: page, size, sort, status, term, showDismissed.
			return ctrl.pageSepaRemittance(currentPage, size, undefined, undefined, term, showDismissed);
		},
		actions: [
			{
				action: prepare,
				color: "primary",
				label: "Preparar remesa",
			},
		],
	};

	return (
		<>
			{/* Sin «Añadir»: una remesa no se crea a mano, sale de «Preparar remesa». */}
			<AppList config={listConfig} appendable={false}/>

			<Panel sx={{mt: 3}} title="Recibos devueltos" subtitle="Últimos 90 días. Vuelven a estar sin cobrar: hay que reclamarlos, no se remesan solos.">
				{returned.length === 0 ? (
					<EmptyState title="Ningún recibo devuelto"/>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size={"small"} sx={{...tableSx, tableLayout: "auto"}}>
							<TableHead>
								<TableRow>
									<TableCell>Factura</TableCell>
									<TableCell>Cliente</TableCell>
									<TableCell align={"right"}>Importe</TableCell>
									<TableCell>Devuelta</TableCell>
									<TableCell>Motivo</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{returned.map((b) => (
									<TableRow key={b.id} hover sx={{cursor: "pointer"}}
											  onClick={() => navigate(`/app/admin/billing/bill/${b.id}`)}>
										<TableCell sx={{fontWeight: 600}}>{b.number}</TableCell>
										<TableCell>{b.customer?.shortName || b.customer?.name}</TableCell>
										<TableCell align={"right"} sx={{whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>
											<CustomNumberFormat amount={b.totals?.total}/> €
										</TableCell>
										<TableCell sx={{whiteSpace: "nowrap"}}>{dateToString(b.returnedDate)}</TableCell>
										<TableCell>{b.returnReason}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Panel>
		</>
	);
};
